import * as React from "react"
import { cn } from "@/lib/utils"
import { CalendarIcon } from "lucide-react"

export interface DateInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  onValueChange?: (value: string) => void
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, value, onChange, onValueChange, disabled, ...props }, ref) => {
    const hiddenRef = React.useRef<HTMLInputElement>(null)

    const combinedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        (hiddenRef as React.MutableRefObject<HTMLInputElement | null>).current = node
        if (typeof ref === "function") ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
      },
      [ref],
    )

    const displayValue = React.useMemo(() => {
      const v = (value ?? "") as string
      if (!v) return ""
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (m) return `${m[1]}-${m[2]}-${m[3]}`
      return v
    }, [value])

    const handlePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      onValueChange?.(e.target.value)
    }

    const openPicker = () => {
      if (disabled) return
      hiddenRef.current?.showPicker?.()
    }

    return (
      <div
        className={cn(
          "relative flex items-center h-10 w-full rounded-md border border-input bg-background text-sm ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span
          className={cn(
            "flex-1 px-3 py-2 select-none cursor-pointer truncate",
            !displayValue && "text-muted-foreground",
          )}
          onClick={openPicker}
        >
          {displayValue || props.placeholder || "YYYY-MM-DD"}
        </span>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          className="px-2 py-2 text-muted-foreground hover:text-foreground shrink-0"
          onClick={openPicker}
          aria-label="날짜 선택"
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
        <input
          ref={combinedRef}
          type="date"
          value={(value ?? "") as string}
          onChange={handlePickerChange}
          disabled={disabled}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          {...props}
        />
      </div>
    )
  },
)
DateInput.displayName = "DateInput"

export { DateInput }
